import {Breadcrumb, ButtonWithIcon} from '@/components/atoms'
import {PromotionsReview} from '@/containers'
import {usePromotionDetail} from '@/hooks/usePromotionDetail'
import {COLOR} from '@/utils/constants/colors'
import {FONT_SIZE, FONT_WEIGHT, SIZE} from '@/utils/constants/globalStyle'
import {ROUTES} from '@/utils/constants/routesConstants'
import {toSafeRouteParam} from '@/utils/security/pathSafety'
import {useNavigate, useParams} from 'react-router-dom'
import {PromotionStatus} from '@/types/promotion'
import {PromotionProvider} from '@/context/Promotion/PromotionContext'
import {PromotionActions} from './PromotionActions'
import './promotionDetail.scss'

/**
 * PromotionDetail component displays detailed information about a single promotion
 * based on the promotion ID from the URL parameters.
 */
const PromotionDetail = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const safeId = toSafeRouteParam(id)

  // Fetch promotion details using React Query
  const {promotionData, isLoading, isError, error} = usePromotionDetail(safeId || id)

  // Breadcrumb configuration
  const breadcrumbUISchema = {
    isDark: true,
    breadcrumbs: [
      {
        href: ROUTES.PROMOTIONS_LIST,
        label: 'Promotions List',
      },
      {
        href: safeId ? `${ROUTES.PROMOTION_DETAIL.replace(':id', safeId)}` : ROUTES.PROMOTIONS_LIST,
        label: promotionData?.name || 'Promotion Detail',
      },
    ],
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="promotion-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading promotion details...</p>
      </div>
    )
  }

  // Handle error state
  if (isError) {
    return (
      <div className="promotion-detail-error">
        <h2>Error loading promotion details</h2>
        <p>{error?.message || 'Unknown error'}</p>
        <button className="ds-button ds-button--secondary" onClick={() => navigate(ROUTES.PROMOTIONS_LIST)}>
          Back to Promotions List
        </button>
      </div>
    )
  }

  // Handle case where data is loaded but empty
  if (!promotionData) {
    return (
      <div className="promotion-detail-not-found">
        <h2>No promotion found</h2>
        <p>The promotion with ID {id} could not be found.</p>
        <button className="ds-button ds-button--secondary" onClick={() => navigate(ROUTES.PROMOTIONS_LIST)}>
          Back to Promotions List
        </button>
      </div>
    )
  }

  const statusText = promotionData.status
    ? `${promotionData.status.charAt(0).toUpperCase() + promotionData.status.slice(1).toLowerCase()}`
    : 'Ongoing'
  // Function to handle step changes for editing
  const handleStepChange = step => {
    navigate(`/products/create-promotion/${promotionData?.id}`, {
      state: {
        promotionType: promotionData.promotionType,
        promotionId: promotionData?.id,
        step: step,
        isEditing: true,
      },
    })
  }

  // Create initial state for PromotionProvider
  const providerState = {
    state: {
      promotionType: promotionData.promotionType,
      promotionId: promotionData?.id,
      isEditing: true,
      isDuplicating: false,
    },
  }

  return (
    <PromotionProvider {...providerState}>
      <div className="promotion-detail-container" data-mcp-ready="promotion-detail">
        {/* Breadcrumb navigation */}
        <Breadcrumb uiSchema={breadcrumbUISchema} id="promotion-detail-breadcrumb" />

        {/* Title, Status and Action buttons */}
        <div className="mb-6 mt-6 flex items-center justify-between">
          <div>
            <h1
              className="mb-2"
              style={{
                fontFamily: 'var(--font-heading, "Noto Sans", sans-serif)',
                fontWeight: FONT_WEIGHT.BOLD,
                fontSize: FONT_SIZE.XXXL,
                lineHeight: '32px',
                letterSpacing: '0px',
              }}
            >
              {promotionData.name}
            </h1>
            <div className="flex items-center">
              <span
                style={{
                  fontFamily: 'var(--font-body, "Noto Sans", sans-serif)',
                  fontWeight: 500,
                  fontSize: FONT_SIZE.SM,
                  lineHeight: '20px',
                  letterSpacing: '0%',
                }}
              >
                {statusText} Promotion
              </span>
            </div>
          </div>
          <PromotionActions promotionData={promotionData} />
        </div>

        {/* Use the PromotionsReview component with the formatted data */}
        <PromotionsReview
          data={promotionData}
          isDetailView={promotionData?.status?.toUpperCase() !== PromotionStatus.DRAFT}
          setCurrentStep={handleStepChange}
          showEditForAll={true}
        />
      </div>
    </PromotionProvider>
  )
}

export default PromotionDetail
